/**
 * AI Gateway WebSocket Service
 *
 * Singleton service that manages the WebSocket connection to the AI Gateway.
 * Provides methods for STT, LLM streaming, TTS, and full voice pipeline.
 */

import { io, Socket } from 'socket.io-client';
import type {
  AIGatewayConnectionState,
  AIGatewayCapabilities,
  AIGatewayError,
  AIGatewayErrorCode,
  AIGatewayRequestEnvelope,
  AIGatewayResponseEnvelope,
  STTAudioPayload,
  STTFinalPayload,
  STTOptions,
  LLMMessage,
  LLMStreamPayload,
  LLMStreamCallbacks,
  GenerationParams,
  TTSSynthesizePayload,
  TTSOptions,
  TTSCallbacks,
  PipelineVoiceTurnPayload,
  PipelineContext,
  PipelineCallbacks,
  PipelineResult,
  PendingRequest,
  SystemConnectedPayload,
} from '../../types/aiGateway';
import { generateRequestId, blobToArrayBuffer, blobToBase64 } from './utils';
import type {
  ScribeStartSessionPayload,
  ScribeAudioChunkPayload,
  ScribeAudioPreviewPayload,
  ScribeEndRecordingPayload,
  ScribeRegeneratePayload,
  ScribeFollowUpPayload,
  ScribeRequestEventHandler,
} from './scribeEvents';

/**
 * Simple browser-compatible EventEmitter
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventCallback = (...args: any[]) => void;

class BrowserEventEmitter {
  private listeners: Map<string, Set<EventCallback>> = new Map();

  on(event: string, callback: EventCallback): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback: EventCallback): void {
    this.listeners.get(event)?.delete(callback);
  }

  emit(event: string, ...args: unknown[]): void {
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(...args);
      } catch (error) {
        console.error(`[EventEmitter] Error in listener for "${event}":`, error);
      }
    });
  }

  removeAllListeners(): void {
    this.listeners.clear();
  }
}


/**
 * TTS audio arrives as base64 on `tts.audio` and as binary on
 * `assistant.tts-audio-chunk` — but the transport encoding has drifted
 * before (HEIDI/backend changes), silently breaking playback. These
 * normalizers make every consumer format-agnostic.
 */
function ttsAudioToBase64(audio: unknown): string {
  if (typeof audio === 'string') return audio;
  if (audio instanceof ArrayBuffer || ArrayBuffer.isView(audio)) {
    const bytes =
      audio instanceof ArrayBuffer
        ? new Uint8Array(audio)
        : new Uint8Array((audio as ArrayBufferView).buffer, (audio as ArrayBufferView).byteOffset, (audio as ArrayBufferView).byteLength);
    let bin = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    return btoa(bin);
  }
  return '';
}

function ttsAudioToArrayBuffer(audio: unknown): ArrayBuffer {
  if (audio instanceof ArrayBuffer) return audio;
  if (ArrayBuffer.isView(audio)) {
    const view = audio as ArrayBufferView;
    return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer;
  }
  if (typeof audio === 'string') {
    const bin = atob(audio);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
  }
  return new ArrayBuffer(0);
}

class AIGatewayService {
  private static instance: AIGatewayService;

  private socket: Socket | null = null;
  private connectionState: AIGatewayConnectionState = 'disconnected';
  private sessionId: string | null = null;
  private capabilities: AIGatewayCapabilities | null = null;
  // `scribe.connection-ready` fires ONCE per socket, during the connect
  // handshake — any listener attached later never sees it. Latch it here
  // so late mounters (WelcomePage chip, useLiveRecording's `ready`) can
  // seed their state via isScribeReady() instead of waiting forever.
  private scribeReady = false;

  // Request tracking
  private pendingRequests: Map<string, PendingRequest> = new Map();
  /** requestId → last stream-event timestamp; drives the idle timeout. */
  private requestActivity: Map<string, number> = new Map();
  private activeRequestCount = 0;
  private readonly maxConcurrentRequests = 10;

  // Reconnection — unbounded retries with capped backoff. Pre-fix this
  // capped at 5 attempts, which meant a brief network blip + a few
  // failed retries permanently bricked the socket until the user
  // refreshed. Idle-killed sockets followed by a slow recovery are
  // the common case; self-healing must outlast them.
  private reconnectAttempts = 0;
  private readonly maxReconnectDelayMs = 30_000;
  private reconnectTimeout: NodeJS.Timeout | null = null;

  // Heartbeat — pong-tracked. The client emits `ping` every
  // `heartbeatIntervalMs` and expects a `pong` back. If two
  // consecutive pongs go missing (NAT idle timeout, half-open TCP,
  // server stall), we proactively tear down the socket and reconnect
  // instead of sitting on a dead-looking-alive connection until the
  // next user action fails. socket.io transport-level disconnect
  // events can take 60–120 s to fire on silent drops; pong-tracking
  // is the only way to detect them sooner.
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private readonly heartbeatIntervalMs = 30000;
  private readonly missedPongThreshold = 2;
  private lastPongAtMs = 0;

  // Event emitter for subscribers
  private eventEmitter = new BrowserEventEmitter();

  // Stream callbacks (for active streaming requests)
  private llmCallbacks: Map<string, LLMStreamCallbacks> = new Map();
  private ttsCallbacks: Map<string, TTSCallbacks> = new Map();
  private pipelineCallbacks: Map<string, PipelineCallbacks> = new Map();

  // In-flight connect promise — dedupes concurrent connect() calls
  private connectPromise: Promise<void> | null = null;

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): AIGatewayService {
    if (!AIGatewayService.instance) {
      AIGatewayService.instance = new AIGatewayService();
    }
    return AIGatewayService.instance;
  }

  /**
   * Connect to the AI Gateway
   */
  async connect(): Promise<void> {
    if (this.connectionState === 'connected' && this.socket?.connected) {
      console.log('[AIGateway] Already connected');
      return;
    }

    // Dedupe concurrent connect() calls — return the in-flight promise
    if (this.connectPromise) {
      return this.connectPromise;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw this.createError('AUTH_FAILED', 'No authentication token found');
    }

    this.connectPromise = new Promise<void>((resolve, reject) => {
      // Bounded initial-handshake retries. The very first WS handshake on
      // a cold page load can transiently fail (proxy upgrade glitch, the
      // gateway namespace still warming, a sibling connect() racing the
      // same singleton) before the path is actually healthy. Pre-fix we
      // rejected the caller on the first `connect_error`, which dropped
      // every AI surface that awaited connect() (summary, assistant,
      // notifications) onto its degraded fallback — the "first load hangs,
      // reload works" symptom. Retry a few times inside this single shared
      // connectPromise so all awaiters self-heal. (This is the FE↔Backend
      // socket; it is independent of HEIDI being warm.) Background
      // reconnects (reconnectAttempts > 0) keep their existing behavior —
      // the disconnect handler drives the capped backoff.
      const maxHandshakeAttempts = 3;
      let handshakeAttempt = 0;
      let settled = false;
      const settleResolve = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      const settleReject = (err: AIGatewayError) => {
        if (settled) return;
        settled = true;
        reject(err);
      };

      const openSocket = () => {
        handshakeAttempt++;
        this.setConnectionState('connecting');

        // Clean up any socket left over from a prior failed attempt.
        if (this.socket) {
          this.cleanup();
        }

        const rawBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) || 'http://localhost:3000';

        // The HTTP base may be a relative path (e.g. `/api`) when the dev
        // server proxies HTTP through Vite. Socket.IO needs an absolute
        // origin to open the WebSocket; if we pass it the relative path,
        // it falls back to `window.location.origin` (the Vite dev server)
        // and the WS never reaches the backend.
        let socketHost: string;
        let socketPath: string;
        if (/^https?:\/\//i.test(rawBase)) {
          // Absolute backend URL — connect directly with default Socket.IO path.
          socketHost = rawBase;
          socketPath = '/socket.io/';
        } else {
          // Relative path — go through the Vite WS-aware proxy. The proxy is
          // configured as `/api → http://localhost:3000` with `ws: true` and a
          // rewrite that strips `/api`, so we mount the Socket.IO endpoint at
          // `/api/socket.io/` on the dev server and the proxy forwards it.
          socketHost = window.location.origin;
          socketPath = `${rawBase.replace(/\/$/, '')}/socket.io/`;
        }

        // Re-read on each attempt so a token refreshed by handleAuthError
        // (or a tenant switch) is picked up on the retry.
        const freshToken = localStorage.getItem('accessToken') ?? token;
        const activeTenantId = localStorage.getItem('activeTenantId') ?? undefined;
        this.socket = io(`${socketHost}/ws/ai-gateway`, {
          transports: ['websocket'],
          path: socketPath,
          auth: { token: freshToken, tenantId: activeTenantId },
          reconnection: false, // Handle reconnection manually
          timeout: 10000,
        });

        // Connection handlers
        this.socket.on('connect', () => {
          console.log('[AIGateway] Connected');
          this.reconnectAttempts = 0;
        });

        this.socket.on('system.connected', (data: AIGatewayResponseEnvelope<SystemConnectedPayload>) => {
          console.log('[AIGateway] System connected:', data.payload);
          this.sessionId = data.payload.session_id;
          this.capabilities = data.payload.capabilities;
          this.setConnectionState('connected');
          this.startHeartbeat();
          this.eventEmitter.emit('capabilities', this.capabilities);
          settleResolve();
        });

        this.socket.on('connect_error', (error: Error) => {
          // Once the handshake has settled (connected, or terminally
          // rejected), a late connect_error must not retry — that would
          // tear down a live socket. Subsequent drops are the disconnect
          // handler's job.
          if (settled) return;

          console.error(
            `[AIGateway] Connection error (attempt ${handshakeAttempt}/${maxHandshakeAttempts}):`,
            error.message,
          );

          // Initial handshake: retry a few times before surfacing the
          // failure. Background reconnects (reconnectAttempts > 0) are
          // driven by the disconnect handler, not here, and must not
          // reject an awaiting caller — preserve that.
          if (this.reconnectAttempts === 0 && handshakeAttempt < maxHandshakeAttempts) {
            this.socket?.disconnect();
            this.socket = null;
            const delay = 400 * handshakeAttempt;
            setTimeout(() => {
              // Bail out if something else connected us in the meantime.
              if (settled || this.isConnected()) {
                settleResolve();
                return;
              }
              openSocket();
            }, delay);
            return;
          }

          this.setConnectionState('error');
          if (this.reconnectAttempts === 0) {
            settleReject(this.createError('CONNECTION_ERROR', error.message));
          }
        });

        this.socket.on('disconnect', (reason: string) => {
          console.log('[AIGateway] Disconnected:', reason);
          this.scribeReady = false;
          this.stopHeartbeat();

          if (reason === 'io server disconnect') {
            // Server disconnected, don't reconnect
            this.setConnectionState('disconnected');
          } else {
            // Attempt reconnection
            this.attemptReconnect();
          }
        });

        // Auth-error path. Backend emits `auth_error` with code
        // WS_UNAUTHORIZED when the JWT is invalid, expired, or missing
        // a tenant claim. Pre-fix the socket would silently disconnect
        // and the EMR sat there. Now: emit a typed event the rest of
        // the app can listen to (RecordingDock, Bridge) AND attempt a
        // token refresh — on success the auth store re-issues a new
        // access token and the next reconnect succeeds; on failure we
        // surface a hard sign-out flag the auth store reads on next
        // tick. Importing useAuthStore lazily avoids a circular dep.
        this.socket.on(
          'auth_error',
          (data: { code?: string; message?: string }) => {
            console.warn('[AIGateway] Auth error:', data);
            this.setConnectionState('error');
            this.eventEmitter.emit('auth_error', data);
            // Don't retry the handshake on an auth failure — a bad/expired
            // token will just fail again. Surface it so the caller can
            // degrade, and let handleAuthError drive the token refresh +
            // a fresh reconnect.
            settleReject(this.createError('AUTH_FAILED', data.message || 'WebSocket unauthorized'));
            void this.handleAuthError();
          },
        );

        // Register event handlers
        this.registerEventHandlers();
      };

      openSocket();

      // Overall connection timeout — covers all handshake attempts.
      setTimeout(() => {
        if (!settled && this.connectionState === 'connecting') {
          this.socket?.disconnect();
          settleReject(this.createError('CONNECTION_ERROR', 'Connection timeout'));
        }
      }, 15000);
    }).finally(() => {
      this.connectPromise = null;
    });

    return this.connectPromise;
  }

  /**
   * Disconnect from the AI Gateway
   */
  disconnect(): void {
    this.cleanup();
    this.setConnectionState('disconnected');
  }

  /**
   * Get the current connection state
   */
  getConnectionState(): AIGatewayConnectionState {
    return this.connectionState;
  }

  /**
   * Get the current capabilities
   */
  getCapabilities(): AIGatewayCapabilities | null {
    return this.capabilities;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionState === 'connected' && this.socket?.connected === true;
  }

  /**
   * True when the current socket's STT path was confirmed live
   * (`scribe.connection-ready` seen, no retriable error since). Read
   * this when mounting a listener for that event — it may already have
   * fired on this connection and will not re-fire.
   */
  isScribeReady(): boolean {
    return this.isConnected() && this.scribeReady;
  }

  /**
   * Backend-assigned WebSocket session id (a.k.a. desktop clientId).
   * Used by the phone-pairing flow to tell the backend which desktop
   * session to bind a phone-source token to.
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Kick any phone-source socket currently paired to this desktop's room.
   * Used by the modal when the doctor sees a paired phone they don't
   * recognise — fires `scribe.kick-phone` over the desktop's socket; the
   * gateway disconnects the phone(s).
   */
  scribeKickPhone(): void {
    if (!this.isConnected()) return;
    const requestId = generateRequestId();
    this.emit('scribe.kick-phone', requestId, {});
  }

  // ============================================================================
  // STT Methods
  // ============================================================================

  /**
   * Transcribe audio to text
   */
  // ArrayBuffer version
  // async transcribe(audio: Blob | string, options: STTOptions = {}): Promise<STTFinalPayload> {
  //   console.log('[AIGateway] transcribe called, audio type:', typeof audio, 'size:', audio instanceof Blob ? audio.size : 'N/A');
  //   this.ensureConnected();

  //   const requestId = generateRequestId();
  //   console.log('[AIGateway] Generated request ID:', requestId);

  //   const audioData: string | ArrayBuffer = typeof audio === 'string' ? audio : await blobToArrayBuffer(audio);
  //   console.log('[AIGateway] Audio converted to binary, byteLength:', audioData instanceof ArrayBuffer ? audioData.byteLength : 'N/A (string)');

  //   const payload: STTAudioPayload = {
  //     audio: audioData,
  //     is_final: options.isFinal ?? true,
  //     language: options.language ?? 'en',
  //   };
  //   console.log('[AIGateway] STT payload created, language:', payload.language, 'is_final:', payload.is_final);

  //   return new Promise((resolve, reject) => {
  //     this.trackRequest(requestId, 'stt', resolve, reject);
  //     console.log('[AIGateway] Emitting stt.audio event with request ID:', requestId);
  //     this.emit('stt.audio', requestId, payload);
  //   });
  // }

  // Base64 version
  async transcribe(audio: Blob | string, options: STTOptions = {}): Promise<STTFinalPayload> {
    console.log('[AIGateway] transcribe called, audio type:', typeof audio, 'size:', audio instanceof Blob ? audio.size : 'N/A');
    this.ensureConnected();
    const requestId = generateRequestId();
    console.log('[AIGateway] Generated request ID:', requestId);
    const audioBase64 = typeof audio === 'string' ? audio : await blobToBase64(audio);
    console.log('[AIGateway] Audio converted to base64, length:', audioBase64.length);
    const payload: STTAudioPayload = {
      audio: audioBase64,
      is_final: options.isFinal ?? true,
      // 'auto' lets Whisper detect Arabic vs English per chunk. Backend
      // normalizes 'auto' → None before handing to faster-whisper.
      language: options.language ?? 'auto',
    };
    console.log('[AIGateway] STT payload created, language:', payload.language, 'is_final:', payload.is_final);
    return new Promise((resolve, reject) => {
      this.trackRequest(requestId, 'stt', resolve as (value: unknown) => void, reject);
      console.log('[AIGateway] Emitting stt.audio event with request ID:', requestId);
      this.emit('stt.audio', requestId, payload);
    });
  }



  // ============================================================================
  // LLM Methods
  // ============================================================================

  /**
   * Stream a chat completion
   */
  async streamChat(
    messages: LLMMessage[],
    callbacks: LLMStreamCallbacks,
    generationParams?: GenerationParams
  ): Promise<string> {
    this.ensureConnected();

    const requestId = generateRequestId();

    const payload: LLMStreamPayload = {
      messages,
      generation_params: generationParams ?? {
        temperature: 0.7,
        max_tokens: 2048,
      },
    };

    // Store callbacks
    this.llmCallbacks.set(requestId, callbacks);

    return new Promise((resolve, reject) => {
      this.trackRequest(requestId, 'llm', resolve as (value: unknown) => void, reject);

      this.emit('llm.stream', requestId, payload);
    });
  }

  /**
   * Stream a patient AI summary via the gateway.
   * Server builds the prompt from DB, streams tokens back as llm.delta events.
   */
  async streamSummary(
    patientId: string,
    callbacks: LLMStreamCallbacks,
    forceRefresh = false,
  ): Promise<string> {
    this.ensureConnected();

    const requestId = generateRequestId();

    // Reuse llmCallbacks — server emits llm.start/llm.delta/llm.complete
    this.llmCallbacks.set(requestId, callbacks);

    return new Promise((resolve, reject) => {
      this.trackRequest(requestId, 'llm', resolve as (value: unknown) => void, reject);

      this.emit('summary.generate', requestId, { patientId, forceRefresh });
    });
  }

  // ============================================================================
  // TTS Methods
  // ============================================================================

  /**
   * Synthesize text to speech
   */
  async synthesize(
    text: string,
    callbacks: TTSCallbacks,
    options: TTSOptions = {}
  ): Promise<string[]> {
    this.ensureConnected();

    const requestId = generateRequestId();

    const payload: TTSSynthesizePayload = {
      text,
      voice: options.voice ?? 'p230',
      ...(options.speed ? { speed: options.speed } : {}),
    };

    // Store callbacks
    this.ttsCallbacks.set(requestId, callbacks);

    return new Promise((resolve, reject) => {
      this.trackRequest(requestId, 'tts', resolve as (value: unknown) => void, reject);

      this.emit('tts.synthesize', requestId, payload);
    });
  }

  // ============================================================================
  // Pipeline Methods
  // ============================================================================

  /**
   * Execute a full voice turn (STT → LLM → TTS)
   */
  async voiceTurn(
    audio: Blob | string,
    context: PipelineContext,
    callbacks: PipelineCallbacks
  ): Promise<PipelineResult> {
    this.ensureConnected();

    const requestId = generateRequestId();
    const audioData: string | ArrayBuffer = typeof audio === 'string' ? audio : await blobToArrayBuffer(audio);

    const payload: PipelineVoiceTurnPayload = {
      audio: audioData,
      messages: context.messages,
      enable_tts: context.enableTTS ?? true,
      voice: context.voice ?? 'p230',
      generation_params: context.generationParams ?? {
        temperature: 0.7,
        max_tokens: 2048,
      },
    };

    // Store callbacks
    this.pipelineCallbacks.set(requestId, callbacks);

    return new Promise((resolve, reject) => {
      this.trackRequest(requestId, 'pipeline', resolve as (value: unknown) => void, reject);

      this.emit('pipeline.voice_turn', requestId, payload);
    });
  }

  /**
   * Interrupt all active pipelines
   */
  interrupt(): void {
    if (!this.isConnected()) return;

    const requestId = generateRequestId();
    this.emit('pipeline.interrupt', requestId, {});
  }

  // ============================================================================
  // AI Assistant Methods (assistant.*)
  // ============================================================================

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private assistantCallbacks: Map<string, { resolve: (v: any) => void; reject: (e: any) => void; onEvent?: (event: string, data: any) => void }> = new Map();

  /**
   * Send a chat message via the AI assistant.
   * Streams LLM tokens via onDelta, resolves with { fullResponse, contextMeta } on completion.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async assistantChat(payload: { message: string; chatId?: string; patientId?: string; sessionId?: string; context?: any; conversationHistory?: Array<{ role: string; content: string }>; enableTTS?: boolean; ttsVoice?: string; ttsSpeed?: number; newThread?: boolean; images?: string[]; clientMessageId?: string; voiceMode?: boolean; responseStyle?: string }, onEvent?: (event: string, data: any) => void): Promise<any> {
    this.ensureConnected();
    const requestId = generateRequestId();
    return new Promise((resolve, reject) => {
      this.assistantCallbacks.set(requestId, { resolve, reject, onEvent });
      this.trackRequest(requestId, 'assistant', resolve, reject);
      this.emit('assistant.chat', requestId, payload);
    });
  }

  /**
   * Interrupt an in-flight chat request (stops LLM generation + TTS).
   */
  chatInterrupt(): void {
    if (!this.isConnected()) return;
    this.socket?.emit('chat.interrupt');
  }

  /**
   * Request AI medical analysis
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async assistantAnalysis(payload: any): Promise<any> {
    this.ensureConnected();
    const requestId = generateRequestId();
    return new Promise((resolve, reject) => {
      this.assistantCallbacks.set(requestId, { resolve, reject });
      this.trackRequest(requestId, 'assistant', resolve, reject);
      this.emit('assistant.analysis', requestId, payload);
    });
  }

  /**
   * Parse a natural language command
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async assistantParseCommand(payload: { query: string; context?: any }): Promise<any> {
    this.ensureConnected();
    const requestId = generateRequestId();
    return new Promise((resolve, reject) => {
      this.assistantCallbacks.set(requestId, { resolve, reject });
      this.trackRequest(requestId, 'assistant', resolve, reject);
      this.emit('assistant.parse-command', requestId, payload);
    });
  }

  /**
   * Start a voice session for transcription
   */
  assistantStartSession(payload: { sessionId: string; language: string; transcriptionOnly?: boolean; streamingPartials?: boolean }): void {
    this.ensureConnected();
    const requestId = generateRequestId();
    this.emit('assistant.start-session', requestId, payload);
  }

  /**
   * Send an audio chunk for transcription
   */
  assistantSendAudioChunk(payload: { audioData: ArrayBuffer; sessionId: string }): void {
    this.ensureConnected();
    const requestId = generateRequestId();
    this.emit('assistant.audio-chunk', requestId, payload);
  }

  /**
   * End a voice recording session
   */
  assistantEndRecording(payload: { sessionId: string }): void {
    this.ensureConnected();
    const requestId = generateRequestId();
    this.emit('assistant.end-recording', requestId, payload);
  }

  /**
   * Stop TTS playback
   */
  assistantStopTts(): void {
    if (!this.isConnected()) return;
    const requestId = generateRequestId();
    this.emit('assistant.stop-tts', requestId, {});
  }

  // ============================================================================
  // Scribe Methods (scribe.*) — Voice-to-SOAP
  // ============================================================================

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private scribeCallbacks: Map<string, { resolve: (v: any) => void; reject: (e: any) => void; onEvent?: ScribeRequestEventHandler }> = new Map();

  /**
   * Start a scribe recording session.
   *
   * `templateId` is forwarded to the backend so SOAP synthesis can be shaped
   * by a specific template (slice C). Until that lands the gateway ignores
   * the field — the frontend only emits it on the wire.
   *
   * `specialty` is forwarded so the backend can pick the right specialty-aware
   * SOAP prompt + hotword pack for this session. When omitted the gateway
   * falls back to the connecting user's `users.specialty`, then to 'general'.
   */
  scribeStartSession(payload: ScribeStartSessionPayload, onEvent?: ScribeRequestEventHandler): void {
    this.ensureConnected();
    const requestId = generateRequestId();
    if (onEvent) this.scribeCallbacks.set(requestId, { resolve: () => {}, reject: () => {}, onEvent });
    this.emit('scribe.start-session', requestId, payload);
  }

  /**
   * Send an audio chunk for scribe transcription
   */
  scribeSendAudioChunk(payload: ScribeAudioChunkPayload): void {
    this.ensureConnected();
    const requestId = generateRequestId();
    this.emit('scribe.audio-chunk', requestId, payload);
  }

  /**
   * Send a fast-preview sub-chunk (dual-cadence live transcript, flag
   * SCRIBE_FAST_PARTIALS). Ephemeral by contract: the gateway transcribes
   * it with `is_final=false` — no diarization, no persistence, no MinIO
   * mirror — and answers with `scribe.transcription-preview`. Losing one
   * is harmless (the canonical 30s chunk carries the same audio), so this
   * is fire-and-forget with no IDB queue or ack watchdog.
   */
  scribeSendAudioPreview(payload: ScribeAudioPreviewPayload): void {
    this.ensureConnected();
    const requestId = generateRequestId();
    this.emit('scribe.audio-preview', requestId, payload);
  }

  /**
   * End a scribe recording and generate SOAP
   */
  scribeEndRecording(payload: ScribeEndRecordingPayload, onEvent?: ScribeRequestEventHandler): void {
    this.ensureConnected();
    const requestId = generateRequestId();
    if (onEvent) this.scribeCallbacks.set(requestId, { resolve: () => {}, reject: () => {}, onEvent });
    this.emit('scribe.end-recording', requestId, payload);
  }

  /**
   * Send a chat message in scribe context
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async scribeChat(payload: { message: string; context?: any; conversationHistory?: Array<{ role: string; content: string }> }): Promise<any> {
    this.ensureConnected();
    const requestId = generateRequestId();
    return new Promise((resolve, reject) => {
      this.scribeCallbacks.set(requestId, { resolve, reject });
      this.trackRequest(requestId, 'scribe', resolve, reject);
      this.emit('scribe.chat', requestId, payload);
    });
  }

  /**
   * Confirm one item from an AI proposal (lab order / imaging order /
   * prescription). Fire-and-forget — the result arrives via the
   * `scribe.confirm-proposal-result` event the bubble listens to.
   * One call = one item; multi-item proposals iterate.
   */
  scribeConfirmProposal(payload: {
    kind: 'lab' | 'imaging' | 'prescription';
    patientId: string;
    patientName?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any;
    itemIndex?: number;
    actionId?: string;
  }): void {
    if (!this.isConnected()) {
      this.eventEmitter.emit('scribe.confirm-proposal-result', {
        ok: false,
        actionId: payload.actionId,
        itemIndex: payload.itemIndex,
        kind: payload.kind,
        error: 'AI Gateway socket not connected',
      });
      return;
    }
    const requestId = generateRequestId();
    this.emit('scribe.confirm-proposal', requestId, payload);
  }

  /**
   * Undo a just-placed proposal order (the conversational "Undo"). Cancels
   * the order created by a prior scribeConfirmProposal. Fire-and-forget —
   * result arrives via `scribe.cancel-proposal-result`. Rx has no entityId
   * (it navigates to a form), so it's never undone here.
   */
  scribeCancelProposal(payload: {
    kind: 'lab' | 'imaging' | 'prescription';
    entityId: string;
    itemIndex?: number;
    actionId?: string;
  }): void {
    if (!this.isConnected()) {
      this.eventEmitter.emit('scribe.cancel-proposal-result', {
        ok: false,
        actionId: payload.actionId,
        itemIndex: payload.itemIndex,
        kind: payload.kind,
        error: 'AI Gateway socket not connected',
      });
      return;
    }
    const requestId = generateRequestId();
    this.emit('scribe.cancel-proposal', requestId, payload);
  }

  /**
   * Extract structured clinical data from text
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async scribeExtractClinicalData(payload: { sessionId: string; text: string; expectedFields: string[] }): Promise<any> {
    this.ensureConnected();
    const requestId = generateRequestId();
    return new Promise((resolve, reject) => {
      this.scribeCallbacks.set(requestId, { resolve, reject });
      this.trackRequest(requestId, 'scribe', resolve, reject);
      this.emit('scribe.extract-clinical-data', requestId, payload);
    });
  }

  /**
   * Regenerate SOAP note from edited transcript.
   *
   * `templateId` lets the caller re-synthesize against a different template
   * without re-recording. Forwarded as-is; backend will honor it once
   * slice C lands.
   */
  scribeRegenerate(payload: ScribeRegeneratePayload, onEvent?: ScribeRequestEventHandler): void {
    this.ensureConnected();
    const requestId = generateRequestId();
    if (onEvent) this.scribeCallbacks.set(requestId, { resolve: () => {}, reject: () => {}, onEvent });
    this.emit('scribe.regenerate', requestId, payload);
  }

  /**
   * Send a follow-up question to refine SOAP
   */
  scribeFollowUp(payload: ScribeFollowUpPayload, onEvent?: ScribeRequestEventHandler): void {
    this.ensureConnected();
    const requestId = generateRequestId();
    if (onEvent) this.scribeCallbacks.set(requestId, { resolve: () => {}, reject: () => {}, onEvent });
    this.emit('scribe.follow-up', requestId, payload);
  }

  // ============================================================================
  // Event Subscription
  // ============================================================================

  /**
   * Generic event subscription (delegates to internal eventEmitter).
   * Returns an unsubscribe function.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(event: string, callback: (...args: any[]) => void): () => void {
    this.eventEmitter.on(event, callback);
    return () => this.eventEmitter.off(event, callback);
  }

  /**
   * Unsubscribe from an event.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  off(event: string, callback: (...args: any[]) => void): void {
    this.eventEmitter.off(event, callback);
  }

  /**
   * Subscribe to connection state changes
   */
  onConnectionChange(callback: (state: AIGatewayConnectionState) => void): () => void {
    this.eventEmitter.on('connectionChange', callback);
    return () => this.eventEmitter.off('connectionChange', callback);
  }

  /**
   * Subscribe to errors
   */
  onError(callback: (error: AIGatewayError) => void): () => void {
    this.eventEmitter.on('error', callback);
    return () => this.eventEmitter.off('error', callback);
  }

  /**
   * Subscribe to capabilities updates
   */
  onCapabilities(callback: (capabilities: AIGatewayCapabilities) => void): () => void {
    this.eventEmitter.on('capabilities', callback);
    return () => this.eventEmitter.off('capabilities', callback);
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  private registerEventHandlers(): void {
    if (!this.socket) return;

    // STT events
    this.socket.on('stt.final', (response: AIGatewayResponseEnvelope<STTFinalPayload>) => {
      const pending = this.pendingRequests.get(response.request_id);
      if (pending) {
        pending.resolve(response.payload);
        this.completeRequest(response.request_id);
      }
    });

    this.socket.on('stt.interim', (response: AIGatewayResponseEnvelope<{ text: string; language: string }>) => {
      // Interim results don't resolve the promise, just notify
      console.log('[AIGateway] Interim transcription:', response.payload.text);
    });

    // LLM events
    this.socket.on('llm.start', (response: AIGatewayResponseEnvelope<object>) => {
      const callbacks = this.llmCallbacks.get(response.request_id);
      callbacks?.onStart?.();
    });

    this.socket.on('llm.delta', (response: AIGatewayResponseEnvelope<{ content: string }>) => {
      const callbacks = this.llmCallbacks.get(response.request_id);
      callbacks?.onDelta?.(response.payload.content);
    });

    this.socket.on('llm.complete', (response: AIGatewayResponseEnvelope<{ full_response: string; token_count: number; finish_reason: string }>) => {
      const callbacks = this.llmCallbacks.get(response.request_id);
      callbacks?.onComplete?.(response.payload.full_response, response.payload.token_count);

      const pending = this.pendingRequests.get(response.request_id);
      // 'assistant' requests must finalize on assistant.chat-complete, which
      // carries the parsed `actions` (proposal / document cards) + the
      // marker-stripped message. llm.complete fires FIRST and only has the
      // raw full_response STRING — resolving the assistant promise here won
      // the race and silently dropped every action, so proposal / document
      // cards never rendered (the reply text still showed, from the streamed
      // snapshot). chat-complete handles resolve + completeRequest for those.
      if (pending && pending.type !== 'assistant') {
        pending.resolve(response.payload.full_response);
        this.completeRequest(response.request_id);
      }

      this.llmCallbacks.delete(response.request_id);
    });

    // TTS events
    this.socket.on('tts.start', (response: AIGatewayResponseEnvelope<object>) => {
      const callbacks = this.ttsCallbacks.get(response.request_id);
      callbacks?.onStart?.();
    });

    this.socket.on('tts.audio', (response: AIGatewayResponseEnvelope<{ audio: string; index: number; total: number; text: string }>) => {
      const callbacks = this.ttsCallbacks.get(response.request_id);
      callbacks?.onAudioChunk?.(
        ttsAudioToBase64(response.payload.audio),
        response.payload.index,
        response.payload.total,
        response.payload.text
      );
    });

    this.socket.on('tts.complete', (response: AIGatewayResponseEnvelope<object>) => {
      const callbacks = this.ttsCallbacks.get(response.request_id);
      callbacks?.onComplete?.();

      const pending = this.pendingRequests.get(response.request_id);
      if (pending) {
        pending.resolve([]);
        this.completeRequest(response.request_id);
      }

      this.ttsCallbacks.delete(response.request_id);
    });

    // Pipeline events
    this.socket.on('pipeline.started', (response: AIGatewayResponseEnvelope<{ stage: 'stt' | 'llm' | 'tts' }>) => {
      const callbacks = this.pipelineCallbacks.get(response.request_id);
      callbacks?.onStageChange?.(response.payload.stage);
    });

    this.socket.on('pipeline.complete', (response: AIGatewayResponseEnvelope<{ transcription: string; response: string; tts_enabled: boolean }>) => {
      const callbacks = this.pipelineCallbacks.get(response.request_id);
      const result: PipelineResult = {
        transcription: response.payload.transcription,
        response: response.payload.response,
        ttsEnabled: response.payload.tts_enabled,
      };
      callbacks?.onComplete?.(result);

      const pending = this.pendingRequests.get(response.request_id);
      if (pending) {
        pending.resolve(result);
        this.completeRequest(response.request_id);
      }

      this.pipelineCallbacks.delete(response.request_id);
    });

    this.socket.on('pipeline.interrupted', (response: AIGatewayResponseEnvelope<{ message: string }>) => {
      const pending = this.pendingRequests.get(response.request_id);
      if (pending) {
        pending.reject(this.createError('PIPELINE_INTERRUPTED', response.payload.message));
        this.completeRequest(response.request_id);
      }
      this.pipelineCallbacks.delete(response.request_id);
    });

    // Forward STT/LLM/TTS events to pipeline callbacks when in pipeline mode
    this.socket.on('stt.final', (response: AIGatewayResponseEnvelope<STTFinalPayload>) => {
      const callbacks = this.pipelineCallbacks.get(response.request_id);
      callbacks?.onTranscription?.(response.payload.text, response.payload.confidence);
    });

    this.socket.on('llm.start', (response: AIGatewayResponseEnvelope<object>) => {
      const callbacks = this.pipelineCallbacks.get(response.request_id);
      callbacks?.onLLMStart?.();
    });

    this.socket.on('llm.delta', (response: AIGatewayResponseEnvelope<{ content: string }>) => {
      const callbacks = this.pipelineCallbacks.get(response.request_id);
      callbacks?.onLLMDelta?.(response.payload.content);
    });

    this.socket.on('llm.complete', (response: AIGatewayResponseEnvelope<{ full_response: string }>) => {
      const callbacks = this.pipelineCallbacks.get(response.request_id);
      callbacks?.onLLMComplete?.(response.payload.full_response);
    });

    this.socket.on('tts.start', (response: AIGatewayResponseEnvelope<object>) => {
      const callbacks = this.pipelineCallbacks.get(response.request_id);
      callbacks?.onTTSStart?.();
    });

    this.socket.on('tts.audio', (response: AIGatewayResponseEnvelope<{ audio: string; index: number; total: number }>) => {
      const callbacks = this.pipelineCallbacks.get(response.request_id);
      callbacks?.onTTSAudio?.(
        ttsAudioToBase64(response.payload.audio),
        response.payload.index,
        response.payload.total
      );
    });

    this.socket.on('tts.complete', (response: AIGatewayResponseEnvelope<object>) => {
      const callbacks = this.pipelineCallbacks.get(response.request_id);
      callbacks?.onTTSComplete?.();
    });

    // ---- Assistant response events ----

    // Cross-device thread sync. Server push to the user's room (all of
    // their sockets, fanned out across replicas via Postgres NOTIFY) —
    // NOT tied to a request_id. Payload: { threadId, kind, timestamp }
    // where kind is 'user' | 'assistant' | 'tool'. No message content
    // travels on this event; consumers refetch the thread over REST.
    this.socket.on(
      'assistant.thread-updated',
      (payload: { threadId: string; kind: string; timestamp?: string }) => {
        this.eventEmitter.emit('assistant.thread-updated', payload);
      },
    );

    // Streaming chat events (new patient Q&A path)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('assistant.chat-start', (response: AIGatewayResponseEnvelope<any>) => {
      this.touchRequest(response.request_id);
      const cb = this.assistantCallbacks.get(response.request_id);
      cb?.onEvent?.('chat-start', response.payload);
      this.eventEmitter.emit('assistant.chat-start', response.payload);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('llm.start', (response: AIGatewayResponseEnvelope<any>) => {
      this.touchRequest(response.request_id);
      const cb = this.assistantCallbacks.get(response.request_id);
      cb?.onEvent?.('llm-start', response.payload);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('llm.delta', (response: AIGatewayResponseEnvelope<any>) => {
      this.touchRequest(response.request_id);
      const cb = this.assistantCallbacks.get(response.request_id);
      cb?.onEvent?.('llm-delta', response.payload);
      this.eventEmitter.emit('llm.delta', response.payload);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('llm.complete', (response: AIGatewayResponseEnvelope<any>) => {
      this.touchRequest(response.request_id);
      const cb = this.assistantCallbacks.get(response.request_id);
      cb?.onEvent?.('llm-complete', response.payload);
      this.eventEmitter.emit('llm.complete', response.payload);
    });

    // Tool-round lifecycle: payload { name, label } on start,
    // { name, status: 'ok'|'error', label, summary } on result. The
    // workspace renders these as a status chip with a spinner /
    // expandable card. Tool execution (e.g. web search) can be slow
    // and emits no llm deltas meanwhile — touching here keeps the idle
    // timeout honest across the gap.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('assistant.tool-start', (response: AIGatewayResponseEnvelope<any>) => {
      this.touchRequest(response.request_id);
      const cb = this.assistantCallbacks.get(response.request_id);
      cb?.onEvent?.('tool-start', response.payload);
      this.eventEmitter.emit('assistant.tool-start', response.payload);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('assistant.tool-result', (response: AIGatewayResponseEnvelope<any>) => {
      this.touchRequest(response.request_id);
      const cb = this.assistantCallbacks.get(response.request_id);
      cb?.onEvent?.('tool-result', response.payload);
      this.eventEmitter.emit('assistant.tool-result', response.payload);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('assistant.chat-complete', (response: AIGatewayResponseEnvelope<any>) => {
      const cb = this.assistantCallbacks.get(response.request_id);
      if (cb) {
        cb.resolve(response.payload);
        this.assistantCallbacks.delete(response.request_id);
        this.completeRequest(response.request_id);
      }
      this.eventEmitter.emit('assistant.chat-complete', response.payload);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('assistant.chat-interrupted', (response: AIGatewayResponseEnvelope<any>) => {
      const cb = this.assistantCallbacks.get(response.request_id);
      if (cb) {
        cb.resolve({ interrupted: true });
        this.assistantCallbacks.delete(response.request_id);
        this.completeRequest(response.request_id);
      }
      this.eventEmitter.emit('assistant.chat-interrupted', response.payload);
    });

    // Legacy non-streaming chat response (kept for backward compat during migration)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('assistant.chat-response', (response: AIGatewayResponseEnvelope<any>) => {
      const cb = this.assistantCallbacks.get(response.request_id);
      if (cb) {
        cb.resolve(response.payload);
        this.assistantCallbacks.delete(response.request_id);
        this.completeRequest(response.request_id);
      }
      this.eventEmitter.emit('assistant.chat-response', response.payload);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('assistant.analysis-response', (response: AIGatewayResponseEnvelope<any>) => {
      const cb = this.assistantCallbacks.get(response.request_id);
      if (cb) {
        cb.resolve(response.payload);
        this.assistantCallbacks.delete(response.request_id);
        this.completeRequest(response.request_id);
      }
      this.eventEmitter.emit('assistant.analysis-response', response.payload);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('assistant.command-parsed', (response: AIGatewayResponseEnvelope<any>) => {
      const cb = this.assistantCallbacks.get(response.request_id);
      if (cb) {
        cb.resolve(response.payload);
        this.assistantCallbacks.delete(response.request_id);
        this.completeRequest(response.request_id);
      }
      this.eventEmitter.emit('assistant.command-parsed', response.payload);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('assistant.connection-ready', (response: AIGatewayResponseEnvelope<any>) => {
      this.eventEmitter.emit('assistant.connection-ready', response.payload);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('assistant.transcription', (response: AIGatewayResponseEnvelope<any>) => {
      this.eventEmitter.emit('assistant.transcription', response.payload);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('assistant.transcription-complete', (response: AIGatewayResponseEnvelope<any>) => {
      this.eventEmitter.emit('assistant.transcription-complete', response.payload);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('assistant.tts-audio-chunk', (response: AIGatewayResponseEnvelope<any>) => {
      const cb = this.assistantCallbacks.get(response.request_id);
      const chunkPayload = {
        ...response.payload,
        audio: ttsAudioToArrayBuffer(response.payload?.audio),
      };
      cb?.onEvent?.('tts-audio-chunk', chunkPayload);
      this.eventEmitter.emit('assistant.tts-audio-chunk', chunkPayload);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.socket.on('assistant.tts-complete', (response: AIGatewayResponseEnvelope<any>) => {
      const cb = this.assistantCallbacks.get(response.request_id);
      cb?.onEvent?.('tts-complete', response.payload);
      this.eventEmitter.emit('assistant.tts-complete', response.payload);
    });

    // ---- Scribe response events ----
    const scribeEvents = [
      'scribe.session-started', 'scribe.chunk-received', 'scribe.transcription-partial',
      // Dual-cadence fast pass: undiarized sub-chunk text that the live
      // pane paints in a muted "pending" style until the canonical 30s
      // diarized partial replaces it. Flag-gated (SCRIBE_FAST_PARTIALS).
      'scribe.transcription-preview',
      // Rolling clinical fact ledger, pushed ~every 2 minutes of audio
      // while capture runs (flag EXTENDED_RECORDING_LEDGER). Carries the
      // whole ACTIVE fact set each time — subscribers replace, never
      // append, because a fact can be retracted mid-consult.
      'scribe.ledger-updated',
      'scribe.processing', 'scribe.processing-update', 'scribe.soap-result',
      'scribe.chat-transcription', 'scribe.chat-response', 'scribe.extraction-result',
      'scribe.follow-up-response', 'scribe.connection-ready', 'scribe.error',
      'scribe.validation-stage',
      // Post-process pass — fired when the BG full-audio re-transcribe
      // + re-diarize completes. Carries the polished transcript and an
      // elapsed_ms hint. Bridge updates the synth-jobs store; review
      // page swaps the displayed transcript and surfaces a "Regenerate
      // from refined transcript" affordance.
      'scribe.transcript-refined',
      // Phone-as-mic pairing lifecycle. Backend emits phone-paired when
      // the phone redeems the QR code, phone-disconnected when the phone
      // socket drops (without ending the session). Desktop UI uses these
      // to flip the QR modal between waiting / connected / reconnecting.
      'scribe.phone-paired',
      'scribe.phone-disconnected',
      // Phone tapped Start; carries phone's wall-clock startedAt so the
      // desktop modal can anchor its recording timer immediately rather
      // than wait for the first chunk to arrive (~30s lag).
      'scribe.phone-recording-started',
      // Phone tapped Pause / Resume — pure UI relay. Desktop reflects
      // the pause state in its recorder card (timer pinned, paused
      // indicator) without touching the audio pipeline.
      'scribe.phone-recording-paused',
      'scribe.phone-recording-resumed',
      // Server signals the whole room (desktop + phone) that the
      // session was finalized — e.g. desktop hit Stop. Phone listens to
      // tear down its local capture so it doesn't keep streaming chunks
      // after the consult is over.
      'scribe.session-ended',
      // Phone-side: desktop pressed Cancel on the pairing. Used to
      // surface an explicit "you were kicked" UI on the phone instead
      // of leaving it stuck on a stale connection.
      'scribe.phone-kicked',
      // Backend tells this client that another start-session for the same
      // account was accepted; this socket's scribe state has been cleared.
      // Used to enforce "one active scribe per account".
      'scribe.session-superseded',
      // AI chat proposal-confirm round trip. Emitted in response to
      // `scribe.confirm-proposal` from the EMR's chat bubble after the
      // doctor taps "Add to patient" on a propose_* action card.
      'scribe.confirm-proposal-result',
    ];
    for (const eventName of scribeEvents) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.socket.on(eventName, (raw: any) => {
        // Backend scribe events are emitted as raw payloads (not envelope-wrapped
        // like LLM events). Tolerate both shapes: if a `payload` field exists,
        // unwrap it; otherwise treat the whole object as the payload.
        const isEnvelope = raw && typeof raw === 'object' && 'payload' in raw && 'request_id' in raw;
        const payload = isEnvelope ? raw.payload : raw;
        const requestId = isEnvelope ? raw.request_id : undefined;

        // Latch STT-path readiness (see scribeReady field). A retriable
        // scribe.error is the backend's "STT path not confirmed" signal —
        // un-latch so isScribeReady() reflects the true state.
        if (eventName === 'scribe.connection-ready') {
          this.scribeReady = true;
        } else if (eventName === 'scribe.error' && payload?.retriable) {
          this.scribeReady = false;
        }

        // Forward to any registered callback (only meaningful when envelope is present)
        const cb = requestId ? this.scribeCallbacks.get(requestId) : undefined;
        cb?.onEvent?.(eventName, payload);

        // Resolve promise-based callbacks for terminal events
        if (eventName === 'scribe.soap-result' || eventName === 'scribe.chat-response' || eventName === 'scribe.extraction-result' || eventName === 'scribe.follow-up-response') {
          if (cb && requestId) {
            cb.resolve(payload);
            this.scribeCallbacks.delete(requestId);
            this.completeRequest(requestId);
          }
        }

        // Always emit on the event bus for subscribers
        this.eventEmitter.emit(eventName, payload);
      });
    }

    // Notifications — backend emits raw notification rows (not envelope-
    // wrapped) when a row lands on the user's tenant-room. Forward to the
    // event bus so `useNotificationStream` can render toasts and refresh
    // the bell count without holding a separate SSE connection.
    this.socket.on('notification.created', (payload: unknown) => {
      this.eventEmitter.emit('notification.created', payload);
    });

    // Pong — record receipt so the heartbeat watchdog can detect a
    // silently-dropped socket (NAT idle timeout, half-open TCP) and
    // force a reconnect well before socket.io's transport layer would.
    this.socket.on('pong', (response: AIGatewayResponseEnvelope<{ timestamp: string }>) => {
      this.lastPongAtMs = Date.now();
      console.log('[AIGateway] Pong received:', response.payload.timestamp);
    });

    // Error
    this.socket.on('error', (response: AIGatewayResponseEnvelope<{ code: string; message: string; details?: Record<string, unknown> }>) => {
      const error = this.createError(
        response.payload.code as AIGatewayErrorCode,
        response.payload.message,
        response.payload.details
      );
      error.requestId = response.request_id;

      // Reject pending request
      const pending = this.pendingRequests.get(response.request_id);
      if (pending) {
        pending.reject(error);
        this.completeRequest(response.request_id);
      }

      // Notify callbacks
      const llmCallbacks = this.llmCallbacks.get(response.request_id);
      llmCallbacks?.onError?.(error);
      this.llmCallbacks.delete(response.request_id);

      const ttsCallbacks = this.ttsCallbacks.get(response.request_id);
      ttsCallbacks?.onError?.(error);
      this.ttsCallbacks.delete(response.request_id);

      const pipelineCallbacks = this.pipelineCallbacks.get(response.request_id);
      pipelineCallbacks?.onError?.(error);
      this.pipelineCallbacks.delete(response.request_id);

      // Clean up assistant/scribe callbacks
      const assistantCb = this.assistantCallbacks.get(response.request_id);
      if (assistantCb) {
        assistantCb.reject(error);
        this.assistantCallbacks.delete(response.request_id);
      }
      const scribeCb = this.scribeCallbacks.get(response.request_id);
      if (scribeCb) {
        scribeCb.reject(error);
        this.scribeCallbacks.delete(response.request_id);
      }

      // Emit global error
      this.eventEmitter.emit('error', error);
    });
  }

  private emit<T>(event: string, requestId: string, payload: T): void {
    if (!this.socket) return;

    const envelope: AIGatewayRequestEnvelope<T> = {
      event,
      request_id: requestId,
      payload,
    };

    this.socket.emit(event, envelope);
  }

  private trackRequest(
    requestId: string,
    type: PendingRequest['type'],
    resolve: (value: unknown) => void,
    reject: (error: AIGatewayError) => void
  ): void {
    if (this.activeRequestCount >= this.maxConcurrentRequests) {
      reject(this.createError('TOO_MANY_REQUESTS', 'Maximum concurrent requests reached'));
      return;
    }

    this.activeRequestCount++;
    this.pendingRequests.set(requestId, {
      requestId,
      type,
      startTime: Date.now(),
      resolve,
      reject,
    });

    // IDLE timeout, not a wall-clock deadline. A healthy assistant
    // tool turn (LLM round → tool execution → second LLM round) can
    // legitimately run past a fixed 60s while streaming the whole
    // time — the old flat timeout rejected it mid-flight, the UI
    // showed "couldn't reach the AI service", and the finished reply
    // only surfaced after a refresh. Any event for this request
    // (delta, tool-start/result, chat-start) calls touchRequest() and
    // pushes the deadline out; only 60s of true silence rejects.
    const IDLE_TIMEOUT_MS = 60000;
    this.requestActivity.set(requestId, Date.now());
    const armTimeout = (delayMs: number) => {
      setTimeout(() => {
        const pending = this.pendingRequests.get(requestId);
        if (!pending) return;
        const lastActivity =
          this.requestActivity.get(requestId) ?? pending.startTime;
        const idleFor = Date.now() - lastActivity;
        if (idleFor < IDLE_TIMEOUT_MS) {
          armTimeout(IDLE_TIMEOUT_MS - idleFor);
          return;
        }
        pending.reject(this.createError('REQUEST_TIMEOUT', 'Request timed out'));
        this.completeRequest(requestId);
      }, delayMs);
    };
    armTimeout(IDLE_TIMEOUT_MS);
  }

  /** Mark stream activity for a request so the idle timeout re-arms. */
  private touchRequest(requestId: string): void {
    if (this.pendingRequests.has(requestId)) {
      this.requestActivity.set(requestId, Date.now());
    }
  }

  private completeRequest(requestId: string): void {
    if (this.pendingRequests.has(requestId)) {
      this.pendingRequests.delete(requestId);
      this.requestActivity.delete(requestId);
      this.activeRequestCount = Math.max(0, this.activeRequestCount - 1);
    }
  }

  private setConnectionState(state: AIGatewayConnectionState): void {
    this.connectionState = state;
    this.eventEmitter.emit('connectionChange', state);
  }

  private ensureConnected(): void {
    if (!this.isConnected()) {
      throw this.createError('SESSION_NOT_FOUND', 'Not connected to AI Gateway');
    }
  }

  private createError(
    code: AIGatewayErrorCode,
    message: string,
    details?: Record<string, unknown>
  ): AIGatewayError {
    return { code, message, details };
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();

    // Seed the pong timestamp so the first interval doesn't false-fire
    // on a freshly-connected socket that hasn't been pinged yet.
    this.lastPongAtMs = Date.now();

    this.heartbeatInterval = setInterval(() => {
      if (!this.socket?.connected) return;

      // Watchdog: if N consecutive pongs are missing, the socket is
      // effectively dead (NAT idle timeout, half-open TCP, server
      // stall). Force a teardown + reconnect — the regular
      // disconnect handler will pick up from there and the
      // exponential backoff will retry until the path heals.
      const staleFor = Date.now() - this.lastPongAtMs;
      if (staleFor > this.heartbeatIntervalMs * this.missedPongThreshold) {
        console.warn(
          `[AIGateway] Heartbeat stale (${staleFor}ms since last pong); ` +
            'forcing reconnect',
        );
        try {
          this.socket.disconnect();
        } catch {
          /* ignore */
        }
        this.stopHeartbeat();
        this.attemptReconnect();
        return;
      }

      this.emit('ping', 'ping', {});
    }, this.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * React to a backend `auth_error`. Try to silently rotate the
   * access token via the auth store's refresh flow; on success the
   * store rewrites localStorage and the next reconnect picks up the
   * new token. On failure, log the user out — the WS will never auth
   * with this token, so leaving them dangling on a closed socket is
   * worse than punting them to the login screen.
   */
  private async handleAuthError(): Promise<void> {
    try {
      // Lazy import — AIGatewayService is loaded before the auth
      // store is fully constructed in some bundling orders, and a
      // top-level import would create a circular dep.
      const { useAuthStore } = await import('@/stores/useAuthStore');
      const auth = useAuthStore.getState();
      if (typeof auth.refreshAuth === 'function') {
        await auth.refreshAuth();
        // Token rotated; tear down the dead socket and let the
        // standard reconnect loop attach with the fresh token.
        try {
          this.socket?.disconnect();
        } catch {
          /* ignore */
        }
        this.attemptReconnect();
        return;
      }
    } catch (refreshErr) {
      console.warn(
        '[AIGateway] Token refresh after auth_error failed; signing out',
        refreshErr,
      );
    }
    // Refresh failed (or not available) — bail to login.
    try {
      const { useAuthStore } = await import('@/stores/useAuthStore');
      await useAuthStore.getState().logout();
    } catch {
      /* ignore — even if logout fails the next request will 401 */
    }
    if (typeof window !== 'undefined') {
      // Replace, not push, so the user can't navigate "back" into
      // the broken authenticated state.
      window.location.replace('/login?reason=session_expired');
    }
  }

  private attemptReconnect(): void {
    // No attempt cap. Self-healing must outlast the kind of multi-minute
    // outage that idle-killed sockets cause (NAT timeout + slow upstream
    // restart). Backoff is capped at maxReconnectDelayMs so we don't
    // hammer a known-down server, but we never stop trying — the only
    // terminal state is an explicit auth_error (handled separately) or
    // a user-initiated disconnect().
    if (this.reconnectTimeout) return; // already scheduled

    this.reconnectAttempts++;
    this.setConnectionState('reconnecting');

    const delay = Math.min(
      1000 * Math.pow(2, Math.min(this.reconnectAttempts - 1, 5)),
      this.maxReconnectDelayMs,
    );
    console.log(
      `[AIGateway] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`,
    );

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect().catch((error) => {
        console.error('[AIGateway] Reconnection failed:', error);
        this.attemptReconnect();
      });
    }, delay);
  }

  private cleanup(): void {
    this.stopHeartbeat();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Reject all pending requests
    this.pendingRequests.forEach((pending) => {
      pending.reject(this.createError('SESSION_NOT_FOUND', 'Connection closed'));
    });
    this.pendingRequests.clear();
    this.activeRequestCount = 0;

    // Clear callbacks
    this.llmCallbacks.clear();
    this.ttsCallbacks.clear();
    this.pipelineCallbacks.clear();
    this.assistantCallbacks.clear();
    this.scribeCallbacks.clear();

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.sessionId = null;
    this.capabilities = null;
    this.scribeReady = false;
  }
}

// Export singleton instance
export const aiGatewayService = AIGatewayService.getInstance();
export default AIGatewayService;
